import { useEffect, useState } from "react";
import { AlertOctagon, LogOut, CreditCard, RefreshCw, Mail, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { BonificoInfo } from "@/components/CustomPlanAbbonamento";

const SUPPORT_EMAIL = "info@incassi.cloud";
const SUPPORT_SUBJECT = "Assistenza attivazione abbonamento Incassi Cloud";

/**
 * Pagina fullscreen mostrata quando l'accesso è bloccato dal middleware
 * backend (HTTP 402): trial scaduto, past_due oltre grace, canceled, expired.
 *
 * Fonte dati piano: SOLO `GET /api/billing/plan` (backend è l'unica autorità).
 * Nessun fallback automatico: se il piano manca o è sconosciuto, mostriamo
 * "Piano non disponibile" e mandiamo l'utente a scegliere in /admin/abbonamento.
 * Non creiamo Checkout Session automaticamente: la CTA "Attiva ora" porta a
 * /admin/abbonamento dove l'utente conferma piano e prezzo prima del pagamento.
 */
export default function AbbonamentoBloccato() {
  const { user, logout, branding } = useAuth();
  const nav = useNavigate();
  const [checking, setChecking] = useState(false);
  const [planLoading, setPlanLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [planError, setPlanError] = useState(false);
  const isAgente = user?.role === "agente";
  const bs = branding?.billing_status || {};
  const isTrialExpired = !!bs.trial_expired;

  useEffect(() => {
    // If already unblocked, redirect out
    if (bs && !bs.blocked && !bs.in_grace_period && bs.plan_status &&
        !["past_due", "canceled", "expired", "unpaid", "pending"].includes(bs.plan_status)) {
      nav(isAgente ? "/admin" : "/foglio-cassa", { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!isAgente) { setPlanLoading(false); return; }
    // /api/billing/plan è unica fonte per piano / prezzo / periodicità.
    // Whitelist middleware già consente questo endpoint anche in blocco.
    api.get("/billing/plan")
      .then((r) => setPlan(r?.data || null))
      .catch(() => setPlanError(true))
      .finally(() => setPlanLoading(false));
  }, [isAgente]);

  const recheck = async () => {
    setChecking(true);
    try {
      const r = await api.get("/agency/public");
      const status = r?.data?.billing_status;
      if (status && !status.blocked) {
        window.location.href = isAgente ? "/admin" : "/foglio-cassa";
        return;
      }
    } catch { /* keep waiting */ }
    finally { setChecking(false); }
  };

  const planIsValid = plan && plan.plan_id && plan.plan_name &&
                      (plan.price_total != null || plan.price_net != null);
  const priceEUR = planIsValid && (plan.price_total ?? plan.price_net);
  const priceStr = priceEUR != null
    ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(priceEUR)
    : null;

  const isCustom = !!plan?.is_custom;
  const title = isTrialExpired ? "Periodo di prova terminato" : isCustom ? "Canone non saldato" : "Abbonamento sospeso";
  const subtitle = branding?.nome_agenzia || "Incassi Cloud";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="abbonamento-bloccato">
      <div className="max-w-lg w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-11 h-11 rounded-xl border flex items-center justify-center ${isTrialExpired ? "bg-amber-50 border-amber-100" : "bg-red-50 border-red-100"}`}>
            {isTrialExpired
              ? <Clock className="w-6 h-6 text-amber-600" />
              : <AlertOctagon className="w-6 h-6 text-red-600" />}
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold text-slate-900 leading-tight" data-testid="bloccato-title">
              {title}
            </h1>
            <div className="text-sm text-slate-500">{subtitle}</div>
          </div>
        </div>

        {isAgente ? (
          <p className="text-sm text-slate-700 leading-relaxed">
            {isCustom ? (
              <>Il canone del tuo piano personalizzato risulta scaduto e il periodo di
              tolleranza è terminato. <b>I tuoi dati restano conservati</b>: effettua il
              bonifico alle coordinate qui sotto e l&apos;accesso verrà riattivato appena
              registreremo il pagamento.</>
            ) : isTrialExpired ? (
              <>Il periodo di prova gratuito è terminato. <b>I tuoi dati restano
              conservati integralmente</b>, ma le funzioni operative sono bloccate
              fino all&apos;attivazione dell&apos;abbonamento.</>
            ) : (
              <>Il pagamento non è andato a buon fine e il periodo di tolleranza di
              <b> 3 giorni </b> è terminato. <b>I tuoi dati restano conservati</b>,
              ma l&apos;accesso alle funzioni operative è bloccato fino alla
              regolarizzazione.</>
            )}
          </p>
        ) : (
          <p className="text-sm text-slate-700 leading-relaxed">
            L&apos;accesso della tua agenzia è temporaneamente sospeso. Contatta
            il tuo agente titolare: appena l&apos;abbonamento sarà attivo, potrai
            rientrare nella piattaforma.
          </p>
        )}

        {/* Riepilogo piano — solo per agente + solo se dati validi dal backend */}
        {isAgente && (
          <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
            {planLoading ? (
              <div className="text-xs text-slate-400" data-testid="bloccato-plan-loading">Caricamento piano…</div>
            ) : planError ? (
              <div className="text-xs text-red-700" data-testid="bloccato-plan-error">
                Impossibile caricare i dettagli del piano. Puoi comunque contattare l&apos;assistenza qui sotto o riprovare più tardi.
              </div>
            ) : planIsValid ? (
              <div data-testid="bloccato-plan-card">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Piano corrente</div>
                <div className="mt-0.5 flex items-baseline gap-2">
                  <div className="font-heading text-lg font-semibold text-slate-900" data-testid="bloccato-plan-name">
                    {plan.plan_name}
                  </div>
                  {priceStr ? (
                    <div className="text-sm text-slate-600" data-testid="bloccato-plan-price">
                      {priceStr}<span className="text-xs text-slate-500">/{plan.periodicita === "annuale" ? "anno" : "mese"}</span>
                    </div>
                  ) : null}
                </div>
                {plan.retention_label ? (
                  <div className="text-xs text-slate-500 mt-1">Storico conservato per {plan.retention_label}</div>
                ) : null}
                {isCustom && <div className="mt-3"><BonificoInfo bonifico={plan.bonifico} /></div>}
              </div>
            ) : (
              <div data-testid="bloccato-plan-missing">
                <div className="text-xs uppercase tracking-wider text-amber-800 font-semibold">Piano non disponibile</div>
                <div className="text-xs text-slate-600 mt-1">
                  Non risulta un piano associato alla tua agenzia. Scegli un piano dalla
                  pagina Abbonamento per attivare il servizio.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stato tecnico compatto (per supporto tecnico) */}
        <div className="mt-3 text-[11px] text-slate-400" data-testid="bloccato-status-line">
          Stato: <b>{isCustom ? "canone scaduto" : (bs.plan_status || "n/d")}</b>
          {bs.past_due_since ? <> · ritardo dal {new Date(bs.past_due_since).toLocaleDateString("it-IT")}</> : null}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          {isAgente && !isCustom && (
            <Button
              data-testid="bloccato-rinnova-btn"
              onClick={() => nav("/admin/abbonamento")}
              className="bg-slate-900 hover:bg-slate-800 text-white flex-1"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Attiva ora
            </Button>
          )}
          <Button
            variant="outline"
            data-testid="bloccato-refresh-btn"
            onClick={recheck}
            disabled={checking}
            className="flex-1"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${checking ? "animate-spin" : ""}`} />
            {checking ? "Verifico…" : "Ho pagato, ricontrolla"}
          </Button>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-4">
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(SUPPORT_SUBJECT)}`}
            className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5"
            data-testid="bloccato-support-link"
          >
            <Mail className="w-4 h-4" /> Contatta l&apos;assistenza
          </a>
          <button
            type="button"
            data-testid="bloccato-logout-btn"
            onClick={() => { logout().then(() => nav("/login", { replace: true })); }}
            className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5"
          >
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </div>
    </div>
  );
}
